import { Critter } from './critter';
import {
  ClassroomAssignmentDTO,
  IClassroomAssignmentDTO,
  IStudentAssignmentDTO,
} from './student-assignment';
import { IUserDTO } from './user';

export interface IGrade {
  gradeName: string;
  gradePercentage: number;
  gradeRatio: string;
}

export interface IGradeMap {
  gradeName: string;
  gradePercent: number;
}

export class GradeMap {
  grades: IGradeMap[] = [];
  constructor(private readonly map?: IGradeMap[]) {
    if (map) {
      this.grades = map;
    } else {
      this.grades.push({ gradeName: 'A', gradePercent: 90 });
      this.grades.push({ gradeName: 'B', gradePercent: 80 });
      this.grades.push({ gradeName: 'C', gradePercent: 70 });
      this.grades.push({ gradeName: 'D', gradePercent: 60 });
    }
  }

  getGradeByPercentage(percentage: number): string {
    return this.grades
      .sort((n1, n2) => n2.gradePercent - n1.gradePercent)
      .find((n) => percentage > n.gradePercent).gradeName;
  }
}

export class StudentGrade implements IGrade {
  gradeName: string;
  gradePercentage: number;
  gradeRatio: string;
  constructor(public readonly points: number, public readonly perfect: number) {
    const map: GradeMap = new GradeMap();
    if (this.perfect) {
      this.gradePercentage = perfect / points;
      this.gradeRatio = `${points}/${perfect}`;
      this.gradeName = map.getGradeByPercentage(this.gradePercentage);
    } else {
      this.gradePercentage = 0;
      this.gradeRatio = `N/A`;
      this.gradeName = `N/A`;
    }
  }
}

export interface IStudentListItemDTO {
  id: string;
  studentId: string;
  studentName: string;
  studentUserId: string;
  studentEmail: string;
  earnedScore: number;
  totalPossibleScore: number;
  totalNotePoints: number;
  studentClassroom?: ClassroomStudentDTO;
  readonly studentGrade: StudentGrade;
}

export class StudentListItemDTO implements IStudentListItemDTO {
  id: string;
  studentId: string = '';
  studentName: string = '';
  studentUserId: string = '';
  studentEmail: string = '';
  earnedScore: number;
  totalNotePoints: number;
  totalPossibleScore: number;
  studentClassroom?: ClassroomStudentDTO;
  get studentGrade(): StudentGrade {
    return new StudentGrade(this.earnedScore, this.totalPossibleScore);
  }

  constructor(configOverride?: Partial<IStudentListItemDTO>) {
    if (configOverride) {
      Object.assign(this, configOverride);
    }
  }
}

export class StudentClassroomRow {
  id: string;
  studentName: string;
  studentEmail: string;
  grade: string = '';
  percent: string = '';
  constructor(studentListItem: StudentListItemDTO) {
    const { studentAssignments } = studentListItem.studentClassroom.student;
    let totalNotePoints = 0;
    const MAX_UNIT_NOTE_POINTS = 30;
    const sum = studentAssignments
      .filter((studentAssignment) => studentAssignment.completedAt)
      .map((studentAssignment) => studentAssignment.finalScore)
      .reduce((previousValue, currentValue) => previousValue + currentValue, 0);

    const groupedStudentAssignments = studentAssignments.reduce(
      (previousValue, currentValue) => {
        if (
          !previousValue[
            currentValue?.classroomAssignment?.gradeUnitDay?.unitId
          ]
        ) {
          previousValue[
            currentValue?.classroomAssignment?.gradeUnitDay?.unitId
          ] = [];
        }
        previousValue[
          currentValue?.classroomAssignment?.gradeUnitDay?.unitId
        ].push(currentValue);
        return previousValue;
      },
      {},
    );

    for (const unit in groupedStudentAssignments) {
      if (groupedStudentAssignments.hasOwnProperty(unit)) {
        const notePoints = groupedStudentAssignments[unit]
          .map((studentAssignment) => {
            return studentAssignment?.notes?.map((note) => {
              return note?.score?.finalScore || 0;
            });
          })
          .filter((item) => item?.length);

        const merged = [].concat.apply([], notePoints);
        const sumOfNotes = merged.reduce(
          (previousValue, currentValue) => previousValue + currentValue,
          0,
        );
        totalNotePoints +=
          sumOfNotes > MAX_UNIT_NOTE_POINTS ? MAX_UNIT_NOTE_POINTS : sumOfNotes;
      }
    }

    totalNotePoints =
      totalNotePoints < studentListItem.totalNotePoints
        ? totalNotePoints
        : studentListItem.totalNotePoints;
    const earnedScore = studentListItem?.earnedScore
      ? studentListItem?.earnedScore
      : 0;
    const totalPossibleScore = studentListItem?.totalPossibleScore
      ? studentListItem?.totalPossibleScore
      : 0;
    this.percent =
      (
        ((earnedScore + totalNotePoints) / totalPossibleScore || 0) * 100
      ).toFixed(1) + '%';
    this.grade = `${sum + totalNotePoints}/${totalPossibleScore}`;

    Object.assign(this, {
      id: studentListItem.studentId,
      studentName: studentListItem.studentName,
      studentEmail: studentListItem.studentEmail,
    });
  }
}

export interface IStudentListSortDTO {
  classroomId?: string;
  gradingPeriodId?: string;
}

export interface IUpdateStudentRequestDTO {
  name: string;
  password: string;
}

export class UpdateStudentRequestDTO implements IUpdateStudentRequestDTO {
  name: string = '';
  password: string = '';

  constructor(configOverride?: Partial<IUpdateStudentRequestDTO>) {
    if (configOverride) {
      Object.assign(this, configOverride);
    }
  }
}

export interface IClassroomStudentDTO {
  student?: StudentDTO;
  studentId: string;
  classroomId: string;
}

export class ClassroomStudentDTO implements IClassroomStudentDTO {
  student?: StudentDTO;
  studentId: string = '';
  classroomId: string = '';

  constructor(configOverride?: Partial<IClassroomStudentDTO>) {
    if (configOverride) {
      Object.assign(this, configOverride);
    }
  }
}
export interface IStudentUserDTO {
  user: IUserDTO;
  id: string;
  studentName: string;
}

export interface IStudentProfileDTO {
  id: string;
  studentName: string;
  coinPurse: number;
  isEnrolled: boolean;
  critter: Critter;
}

export class StudentProfile implements IStudentProfileDTO {
  id: string = '';
  studentName: string = '';
  coinPurse: number;
  isEnrolled: boolean = false;
  critter: Critter = null;

  constructor(configOverride?: Partial<IStudentProfileDTO>) {
    if (configOverride) {
      Object.assign(this, configOverride);
    }
  }
}

export interface IStudentDTO {
  id: string;
  studentId: string;
  studentName: string;
  studentUserId: string;
  studentEmail: string;
  critter?: Critter;
  studentAssignments?: IStudentAssignmentDTO[];
}

export class StudentDTO implements IStudentDTO {
  id: string = '';
  studentId: string = '';
  studentName: string = '';
  studentUserId: string = '';
  studentEmail: string = '';
  critter?: Critter = null;
  studentAssignments?: IStudentAssignmentDTO[] = [];

  constructor(configOverride?: Partial<IStudentUserDTO>) {
    if (configOverride) {
      Object.assign(this, configOverride);
    }
  }
}

export interface IStudentInviteRequestDTO {
  firstName: string;
  lastName: string;
  email: string;
  password: string;
}

export class StudentInviteRequestDTO implements IStudentInviteRequestDTO {
  firstName: string = '';
  lastName: string = '';
  email: string = '';
  password: string = '';

  constructor(configOverride?: Partial<IStudentInviteRequestDTO>) {
    if (configOverride) {
      Object.assign(this, configOverride);
    }
  }
}
